import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { BookOpen, ClipboardList, LogOut, Home, ShoppingBag, Sun, Moon, Shield } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_fitsmith-academy/artifacts/zbh0gz3y_291E0B11-36FD-4420-BAF1-DDEC0FEFC105.png";

export default function AccountNavbar({ onLogout }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const initials = user?.name?.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() || "?";
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const isLight = theme === "light";

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const isSchemaOnly = user?.plan === "schema";
  const isAdmin = user?.role === "admin";

  return (
    <nav className="bg-[#0a0a0a] border-b border-white/5 px-4 sm:px-8 py-4 flex items-center justify-between">
      <button onClick={() => navigate("/")} className="flex items-center gap-2">
        <img src={LOGO_URL} alt="FitSmith" className="h-8 w-8 object-contain" />
        <span className="text-white font-bold text-lg tracking-wide">FitSmith</span>
      </button>
      <div className="flex items-center gap-2">
        {/* Bekijk aanbod — altijd zichtbaar */}
        <button
          onClick={() => navigate("/")}
          data-testid="nav-website-btn"
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-zinc-400 hover:text-white hover:bg-white/5 transition-colors text-sm font-medium border border-transparent hover:border-white/8"
        >
          <ShoppingBag size={14} className="text-[#C9A84C]" />
          <span className="hidden sm:inline">Bekijk aanbod</span>
        </button>

        <div className="relative" ref={ref}>
          <button
            onClick={() => setOpen(o => !o)}
            data-testid="account-avatar-btn"
            className="w-9 h-9 rounded-full bg-gradient-to-br from-[#C9A84C] to-[#D4AF37] flex items-center justify-center text-black text-xs font-bold hover:ring-2 hover:ring-[#C9A84C]/50 transition-all"
          >
            {initials}
          </button>

          {open && (
            <div className="absolute right-0 top-11 w-48 bg-[#111] border border-white/10 rounded-2xl shadow-2xl overflow-hidden z-50 animate-fadeIn">
              <div className="px-4 py-3 border-b border-white/8">
                <p className="text-white text-xs font-bold truncate">{user?.name}</p>
                <p className="text-zinc-600 text-xs truncate">{user?.email}</p>
              </div>
              <div className="py-1.5">
                <button
                  onClick={() => { navigate("/"); setOpen(false); }}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-zinc-400 hover:text-white hover:bg-white/5 transition-colors text-sm text-left"
                  data-testid="nav-home-btn"
                >
                  <Home size={14} className="text-[#C9A84C]" /> Website
                </button>
                {isAdmin && (
                  <button
                    onClick={() => { navigate("/admin"); setOpen(false); }}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-[#C9A84C] hover:bg-[#C9A84C]/8 transition-colors text-sm text-left font-semibold"
                    data-testid="nav-admin-paneel-btn"
                  >
                    <Shield size={14} /> Admin paneel
                  </button>
                )}
                <button
                  onClick={() => { navigate("/account"); setOpen(false); }}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-zinc-400 hover:text-white hover:bg-white/5 transition-colors text-sm text-left"
                  data-testid="nav-schema-btn"
                >
                  <ClipboardList size={14} className="text-[#C9A84C]" /> Mijn Schema
                </button>
                {!isSchemaOnly && (
                  <button
                    onClick={() => { navigate("/cursus"); setOpen(false); }}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-zinc-400 hover:text-white hover:bg-white/5 transition-colors text-sm text-left"
                    data-testid="nav-cursus-btn"
                  >
                    <BookOpen size={14} className="text-[#C9A84C]" /> Mijn Cursus
                  </button>
                )}
              </div>
              <div className="border-t border-white/8 px-4 py-3">
                <p className="text-zinc-600 text-[10px] uppercase tracking-widest font-bold mb-2">Weergave</p>
                <button
                  onClick={toggleTheme}
                  data-testid="theme-toggle-btn"
                  className={`w-full flex items-center justify-between rounded-xl px-3 py-2.5 transition-all border ${
                    isLight
                      ? "bg-amber-50/10 border-amber-400/30 text-amber-400"
                      : "bg-blue-500/8 border-blue-400/20 text-blue-300"
                  }`}
                >
                  <div className="flex items-center gap-2.5 text-xs font-semibold">
                    {isLight ? <Sun size={13} /> : <Moon size={13} />}
                    {isLight ? "Lichte weergave" : "Donkere weergave"}
                  </div>
                  <div className={`relative w-9 h-5 rounded-full transition-all ${isLight ? "bg-amber-400" : "bg-zinc-700"}`}>
                    <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${isLight ? "left-[18px]" : "left-0.5"}`} />
                  </div>
                </button>
              </div>
              <div className="border-t border-white/8 py-1.5">
                <button
                  onClick={() => { onLogout(); setOpen(false); }}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-zinc-500 hover:text-red-400 hover:bg-red-500/5 transition-colors text-sm text-left"
                  data-testid="account-logout-btn"
                >
                  <LogOut size={14} /> Uitloggen
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
